import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Wallet, ArrowRight, CheckCircle2, Calendar, MapPin } from "lucide-react";

export default function BookingDetail() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then(r => setBooking(r.data)).catch(() => toast.error("Booking not found"));
  }, [bookingId]);

  const payWithStripe = async () => {
    setLoading("stripe");
    try {
      const r = await api.post("/checkout/stripe", { booking_id: bookingId, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe payment failed to start");
      setLoading(false);
    }
  };

  const payWithPayPal = async () => {
    setLoading("paypal");
    try {
      const r = await api.post("/checkout/paypal/create", { booking_id: bookingId, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PayPal payment failed to start");
      setLoading(false);
    }
  };

  if (!booking) return (<div><Navbar/><div className="pt-32 text-center text-[#6b7c8a]">Loading...</div></div>);

  return (
    <div data-testid="booking-detail-page">
      <Navbar/>
      <div className="pt-32 pb-20 px-6 md:px-12 max-w-5xl mx-auto">
        <p className="eyebrow mb-3">Step 5 of 5</p>
        <h1 className="font-serif text-4xl md:text-5xl mb-2">Confirm & <span className="italic gradient-gold">Pay</span></h1>
        <div className="divider"/>

        <div className="grid md:grid-cols-2 gap-8 mt-10">
          <div className="card-dark p-8">
            <p className="eyebrow mb-3">Reservation</p>
            <h3 className="font-serif text-2xl mb-1">{booking.customer_name}</h3>
            <p className="text-[#6b7c8a] text-sm mb-6">{booking.customer_email} · {booking.customer_phone}</p>

            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3 text-[#b8c5d1]"><Calendar size={14} className="text-[#5DCFE5]"/>{booking.pickup_date} at {booking.pickup_time}</div>
              <div className="flex items-start gap-3 text-[#b8c5d1]"><MapPin size={14} className="text-[#5DCFE5] mt-0.5"/><span><b className="text-[#f0f6fb]">From:</b> {booking.pickup_address}</span></div>
              <div className="flex items-start gap-3 text-[#b8c5d1]"><MapPin size={14} className="text-[#5DCFE5] mt-0.5"/><span><b className="text-[#f0f6fb]">To:</b> {booking.dropoff_address}</span></div>
              <div className="text-[#6b7c8a]">Passengers: {booking.passengers}</div>
              {booking.notes && <div className="text-[#6b7c8a] italic pt-2 border-t border-[#1a2838]">"{booking.notes}"</div>}
            </div>

            <div className="border-t border-[#1a2838] mt-6 pt-6 flex justify-between items-center">
              <span className="eyebrow">Total Due</span>
              <span className="font-serif text-4xl text-[#5DCFE5]" data-testid="booking-total">${booking.total_amount.toFixed(2)}</span>
            </div>
          </div>

          <div className="space-y-4">
            <p className="eyebrow">Choose Payment Method</p>

            <button onClick={payWithStripe} disabled={!!loading} data-testid="pay-stripe-btn"
              className="w-full card-dark p-6 flex items-center justify-between hover:border-[#5DCFE5] transition group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#635bff] flex items-center justify-center">
                  <CreditCard size={20} className="text-white"/>
                </div>
                <div className="text-left">
                  <div className="font-medium">{loading === "stripe" ? "Redirecting..." : "Pay with Card"}</div>
                  <div className="text-xs text-[#6b7c8a]">Stripe · Apple Pay · Google Pay</div>
                </div>
              </div>
              <ArrowRight size={18} className="text-[#5DCFE5] group-hover:translate-x-1 transition"/>
            </button>

            <button onClick={payWithPayPal} disabled={!!loading} data-testid="pay-paypal-btn"
              className="w-full card-dark p-6 flex items-center justify-between hover:border-[#5DCFE5] transition group">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#003087] flex items-center justify-center">
                  <Wallet size={20} className="text-white"/>
                </div>
                <div className="text-left">
                  <div className="font-medium">{loading === "paypal" ? "Redirecting..." : "Pay with PayPal"}</div>
                  <div className="text-xs text-[#6b7c8a]">Secure PayPal Checkout</div>
                </div>
              </div>
              <ArrowRight size={18} className="text-[#5DCFE5] group-hover:translate-x-1 transition"/>
            </button>

            <div className="text-xs text-[#4a5868] flex items-start gap-2 pt-4">
              <CheckCircle2 size={14} className="text-[#5DCFE5] mt-0.5 flex-shrink-0"/>
              <span>You will receive a confirmation by email once your payment is processed and your chauffeur is assigned.</span>
            </div>
          </div>
        </div>
      </div>
      <Footer/>
    </div>
  );
}
